import React, { useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import Magnetic from "./Magnetic";
import CountUp from "./CountUp";
import Glyph from "./Glyphs";
import { EMAIL, MAILTO, CopyEmailButton } from "./Email";
import {
  profile,
  focus,
  proof,
  recognition,
  nav,
  about,
  services,
  projects,
  toolkit,
  contact,
  footer,
  socials,
} from "../site";

const Arrow = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 9.5 9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* -------------------------------------------------------------------------- */
/* Hero                                                                       */
/* -------------------------------------------------------------------------- */

export function Hero() {
  return (
    <section className="hero" id="home">
      <span className="hero__glow" aria-hidden="true" />
      <div className="shell hero__inner">
        <h1 className="hero__title">
          {profile.headline.map((line, index) => {
            const isLast = index === profile.headline.length - 1;
            return (
              <span className="hero__line" key={line}>
                <span className={isLast ? "is-accent" : undefined} style={{ "--d": `${140 + index * 110}ms` }}>
                  {line}
                </span>
              </span>
            );
          })}
        </h1>

        <p className="hero__sub">{profile.sub}</p>
      </div>

      {profile.avatar ? (
        <Reveal as="div" delay={60}>
          <span className="hero__avatar">
            <img
              src={profile.avatar}
              alt={`${profile.name}, ${profile.role}`}
              width="200"
              height="200"
              decoding="async"
              fetchpriority="high"
            />
          </span>
        </Reveal>
      ) : null}

      <span className="hero__scroll" aria-hidden="true">
        Scroll
        <i />
      </span>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Focus marquee                                                              */
/* -------------------------------------------------------------------------- */

export function FocusStrip() {
  const group = (
    <div className="marquee__group" aria-hidden="true">
      {focus.map((item) => (
        <span className="marquee__item" key={item}>
          {item}
        </span>
      ))}
    </div>
  );

  return (
    <div className="marquee" role="presentation">
      <div className="marquee__track">
        {group}
        {group}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Proof strip                                                                */
/* -------------------------------------------------------------------------- */

export function ProofStrip() {
  return (
    <section className="proof">
      <div className="shell">
        <div className="proof__grid">
          {proof.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 80}>
              <div className="stat">
                <span className="stat__icon" aria-hidden="true">
                  <Glyph name={stat.icon} />
                </span>
                <span className="stat__value" aria-hidden="true">
                  <CountUp value={stat.value} />
                </span>
                <span className="sr-only">{stat.value}</span>
                <span className="stat__label">{stat.label}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="recognition" delay={120}>
          {recognition.map((item) => (
            <span className="recognition__item" key={item.label}>
              <span className="recognition__icon" aria-hidden="true">
                <Glyph name={item.icon} />
              </span>
              {item.label}
            </span>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* About                                                                      */
/* -------------------------------------------------------------------------- */

export function About() {
  return (
    <section className="section" id="about">
      <div className="shell">
        <Reveal className="section__head">
          <div>
            <span className="label">{about.label}</span>
            <h2 className="section__title">{about.heading}</h2>
          </div>
        </Reveal>

        <div className="about__grid">
          <Reveal className="about__copy" delay={80}>
            {about.paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 24)}>{paragraph}</p>
            ))}
          </Reveal>

          <Reveal className="about__panel" delay={160}>
            <ul className="tools">
              {about.tools.map((tool) => (
                <li className="tools__row" key={tool.label}>
                  <span className="tools__icons" aria-hidden="true">
                    {tool.icons.map((icon) => (
                      <img key={icon} src={`/tools/${icon}.svg`} alt="" width="20" height="20" loading="lazy" decoding="async" />
                    ))}
                  </span>
                  <span className="tools__label">{tool.label}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Services                                                                   */
/* -------------------------------------------------------------------------- */

export function Services() {
  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--mx", `${((event.clientX - rect.left) / rect.width) * 100}%`);
    event.currentTarget.style.setProperty("--my", `${((event.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <section className="section" id="what-i-do">
      <div className="shell">
        <Reveal className="section__head">
          <div>
            <span className="label">{services.label}</span>
            <h2 className="section__title">{services.heading}</h2>
          </div>
        </Reveal>

        <div className="cards cards--three">
          {services.items.map((item, index) => (
            <Reveal key={item.title} delay={index * 90} style={{ display: "flex" }}>
              <article className="card" onMouseMove={handleMove}>
                <div className="card__top">
                  <span className="card__index">{item.index}</span>
                  <span className="card__icon">
                    <Glyph name={item.icon} />
                  </span>
                </div>
                <h3 className="card__title">{item.title}</h3>
                <p className="card__body">{item.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Portfolio                                                                  */
/* -------------------------------------------------------------------------- */

export function Projects() {
  const items = projects.items;
  const [showAll, setShowAll] = useState(false);
  const INITIAL_COUNT = 3;
  const visibleItems = showAll ? items : items.slice(0, INITIAL_COUNT);
  const hasMore = items.length > INITIAL_COUNT;

  return (
    <section className="section" id="portfolio">
      <div className="shell">
        <Reveal className="section__head">
          <div>
            {projects.label ? <span className="label">{projects.label}</span> : null}
            <h2 className="section__title">{projects.heading}</h2>
          </div>
        </Reveal>

        <div className="pcards">
          {items.length ? (
            <>
              {visibleItems.map((item, index) => (
                <Reveal key={item.slug} delay={index * 70}>
                  <Link
                    className="pcard"
                    to={`/portfolio/${item.slug}`}
                    onMouseMove={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      event.currentTarget.style.setProperty("--mx", `${((event.clientX - rect.left) / rect.width) * 100}%`);
                      event.currentTarget.style.setProperty("--my", `${((event.clientY - rect.top) / rect.height) * 100}%`);
                    }}
                  >
                    <span className="pcard__media">
                      <img
                        src={item.image}
                        alt={item.imageAlt || item.title}
                        width={item.imageW || 1024}
                        height={item.imageH || 768}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>

                    <span className="pcard__body">
                      <span className="pcard__meta">
                        <span className="pcard__cat">{item.category}</span>
                        {item.period ? <span className="pcard__period">{item.period}</span> : null}
                      </span>

                      <span className="pcard__title">{item.title}</span>
                      <span className="pcard__line">{item.card}</span>

                      <span className="pcard__more">
                        View project
                        <Arrow />
                      </span>
                    </span>
                  </Link>
                </Reveal>
              ))}

              {hasMore && !showAll && (
                <Reveal delay={INITIAL_COUNT * 70} style={{ gridColumn: '1 / -1' }}>
                  <div className="pcards__more-wrapper">
                    <button
                      className="pcards__more-btn"
                      onClick={() => {
                        setShowAll(true);
                        setTimeout(() => {
                          const newlyAdded = document.querySelectorAll('.pcard:not(.pcard--animate-in)');
                          newlyAdded.forEach((card, i) => {
                            card.classList.add('pcard--animate-in');
                            card.style.animationDelay = `${i * 80}ms`;
                          });
                        }, 50);
                      }}
                    >
                      Show more projects
                    </button>
                  </div>
                </Reveal>
              )}
            </>
          ) : (
            <p className="pcards__empty">Projects are being written up. Check back shortly.</p>
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Toolkit                                                                    */
/* -------------------------------------------------------------------------- */

export function Toolkit() {
  return (
    <section className="section" id="skills">
      <div className="shell">
        <Reveal className="section__head">
          <div>
            <span className="label">{toolkit.label}</span>
            <h2 className="section__title">{toolkit.heading}</h2>
          </div>
        </Reveal>

        <div className="toolkit">
          {toolkit.groups.map((group, index) => (
            <Reveal key={group.name} delay={(index % 3) * 70}>
              <div className="toolgroup">
                <h3 className="toolgroup__name">{group.name}</h3>
                <div className="toolgroup__items">
                  {group.items.map((item) => (
                    <span className="chip chip--tool" key={item.label}>
                      <img
                        className="chip__icon"
                        src={`/tools/${item.icon}.svg`}
                        alt=""
                        width="16"
                        height="16"
                        loading="lazy"
                        decoding="async"
                      />
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Contact                                                                    */
/* -------------------------------------------------------------------------- */

export function Contact() {
  return (
    <section className="section contact" id="contact">
      <div className="shell">
        <Reveal className="contact__panel">
          <span className="label">{contact.label}</span>
          <h2 className="contact__title">{contact.heading}</h2>
          <p className="contact__sub">{contact.sub}</p>

          <div className="contact__actions">
            <Magnetic>
              <a className="btn btn--primary" href={MAILTO}>
                <Glyph name="mail" />
                {contact.ctaLabel}
              </a>
            </Magnetic>
            {contact.secondaryHref ? (
              <Magnetic strength={0.2}>
                <a className="btn btn--ghost" href={contact.secondaryHref} target="_blank" rel="noopener noreferrer">
                  <img className="btn__mark" src="/tools/github.svg" alt="" width="17" height="17" loading="lazy" decoding="async" />
                  {contact.secondaryLabel}
                </a>
              </Magnetic>
            ) : null}
          </div>

          <div className="emailrow">
            <span className="emailrow__icon" aria-hidden="true">
              <Glyph name="mail" />
            </span>
            <a className="emailrow__address" href={MAILTO}>
              {EMAIL}
            </a>
            <CopyEmailButton />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer                                                                     */
/* -------------------------------------------------------------------------- */

export function Footer() {
  const live = socials.filter((social) => social.href);
  const year = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="shell">
        <div className="footer__brand">
          <div>
            <span className="footer__name">{profile.name}</span>
            <p className="footer__line">{footer.line}</p>
          </div>

          <a className="footer__cta" href={MAILTO}>
            <Glyph name="mail" />
            <span>{EMAIL}</span>
          </a>
        </div>

        <div className="footer__row">
          <nav className="footer__nav" aria-label="Footer">
            {nav.map((item) => (
              <Link key={item.href} to={`/${item.href}`}>
                {item.label}
              </Link>
            ))}
          </nav>

          <Link className="footer__top" to="/#home">
            Back to top
            <Glyph name="arrow-up" />
          </Link>
        </div>

        <div className="footer__legal">
          <span>
            © {year} ahsanusman.com. All rights reserved.
          </span>

          <ul className="footer__socials">
            {live.map((social) => (
              <li key={social.label}>
                <a
                  className="footer__social"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  title={social.label}
                >
                  <img
                    src={`/tools/${social.icon}.svg`}
                    alt=""
                    width="18"
                    height="18"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
